import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";

export default function ViewOrder() {
  const { orderId } = useParams();
  const toast = useToast();
  const [order, setOrder] = useState(null);
  const [statusValues, setStatusValues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const openImageModal = (img) => {
    setSelectedImage(img);
    onOpen();
  };

  const fetchOrder = async () => {
    try {
      const res = await axiosInstance.get(`/api/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setOrder(res.data.order);
      setStatusValues(
        res.data.order.orderDetails.map(
          (item) => item.measurements?.status || "in-progress"
        )
      );
    } catch (err) {
      toast({ title: "Failed to fetch order", status: "error" });
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line
  }, [orderId]);

  const handleStatusChange = (index, value) => {
    const updated = [...statusValues];
    updated[index] = value;
    setStatusValues(updated);
  };

  const handleUpdate = async () => {
    try {
      setIsLoading(true);
      for (let i = 0; i < statusValues.length; i++) {
        await axiosInstance.put(
          `/api/orders/${orderId}/status`,
          { status: statusValues[i] },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      toast({ title: "Order status updated", status: "success" });
      fetchOrder();
    } catch (err) {
      toast({ title: "Failed to update status", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  if (!order) return null;

  return (
    <Box p={6} maxW="6xl" mx="auto">
      <Heading size="md" color="red.500" mb={4}>
        Order & Measurement Details
      </Heading>

      <VStack spacing={6} align="stretch">
        {order.orderDetails.map((detail, index) => (
          <Box
            key={index}
            p={4}
            borderWidth="1px"
            borderRadius="lg"
            bg="gray.50"
          >
            <Text fontWeight="bold" color="red.500" mb={2}>
              Order {index + 1}
            </Text>

            {/* Top Row with Quantity Added */}
            <HStack spacing={4} mb={2}>
              <FormControl isReadOnly>
                <FormLabel>Item</FormLabel>
                <Input value={detail.item || ""} readOnly />
              </FormControl>

              <FormControl isReadOnly>
                <FormLabel>Size</FormLabel>
                <Input value={detail.size || ""} readOnly />
              </FormControl>

              <FormControl isReadOnly>
                <FormLabel>Color</FormLabel>
                <Input value={detail.color || ""} readOnly />
              </FormControl>

              {/* ✅ Quantity Field Added */}
              <FormControl isReadOnly>
                <FormLabel>Quantity</FormLabel>
                <Input value={detail.quantity || 1} readOnly />
              </FormControl>
            </HStack>

            <HStack spacing={4} mb={2}>
              <FormControl isReadOnly>
                <FormLabel>Design</FormLabel>
                <Input value={detail.design || ""} readOnly />
              </FormControl>
              <FormControl isReadOnly>
                <FormLabel>Thread</FormLabel>
                <Input value={detail.thread || ""} readOnly />
              </FormControl>
              <FormControl isReadOnly>
                <FormLabel>Lace</FormLabel>
                <Input value={detail.lace || ""} readOnly />
              </FormControl>
            </HStack>

            {/* Image Section */}
            <FormControl isReadOnly mb={2}>
              <FormLabel>Image</FormLabel>
              {detail.image && (
                <Image
                  src={detail.image}
                  alt="Order item"
                  boxSize="100px"
                  objectFit="cover"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => openImageModal(detail.image)}
                />
              )}
            </FormControl>

            {/* Measurements Section */}
            <Heading size="sm" color="red.500" mt={4} mb={2}>
              Measurements
            </Heading>
            <HStack spacing={4} mb={2}>
              <FormControl isReadOnly>
                <FormLabel>Length</FormLabel>
                <Input value={detail.measurements?.length || ""} readOnly />
              </FormControl>
              <FormControl isReadOnly>
                <FormLabel>Waist</FormLabel>
                <Input value={detail.measurements?.waist || ""} readOnly />
              </FormControl>
              <FormControl isReadOnly>
                <FormLabel>Hip</FormLabel>
                <Input value={detail.measurements?.hip || ""} readOnly />
              </FormControl>
              <FormControl isReadOnly>
                <FormLabel>Etc</FormLabel>
                <Input value={detail.measurements?.etc || ""} readOnly />
              </FormControl>
            </HStack>

            {/* Description */}
            <FormControl isReadOnly mb={2}>
              <FormLabel>Description</FormLabel>
              <Input value={detail.description || ""} readOnly />
            </FormControl>

            {/* Status Dropdown */}
            <FormControl mt={2}>
              <FormLabel>Status</FormLabel>
              <Select
                value={statusValues[index]}
                onChange={(e) => handleStatusChange(index, e.target.value)}
              >
                <option value="in-progress">In Progress</option>
                <option value="done">Done</option>
                <option value="cancel">Cancel</option>
              </Select>
            </FormControl>
          </Box>
        ))}

        {/* Submit Button */}
        <Button
          colorScheme="red"
          onClick={handleUpdate}
          isLoading={isLoading}
          loadingText="Updating"
          alignSelf="flex-start"
        >
          Submit Status Changes
        </Button>
      </VStack>

      {/* Image Popup Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" maxW="90vw" maxH="90vh" p={0}>
          <ModalCloseButton color="white" size="lg" />
          <ModalBody
            p={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Image
              src={selectedImage}
              alt="Big View"
              maxW="90vw"
              maxH="90vh"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
